import { C8AttributeId, cnl } from "csi/typings";
import { action, makeObservable, observable } from "mobx";

/**
 * This store contains information about global events
 */


export interface FileUploadInfo {
    abortController: AbortController;
    nodeCNL: cnl;
    fileSize: number;
	attribute: C8AttributeId;
}

export default class BrowserStore {
	/** map of files being uploaded and their abortController objects */
	fileUploadMap = observable.map<cnl, FileUploadInfo>();

	/** indicates if in the middle of editing an attribute */
	@observable editingInline = false;

	/** set to true when in full screen mode */
	@observable fullScreen = false;

	/** name of view if fullscreen view is part of a composite view */
	@observable fullScreenCompositeViewName = "";

	/** force left drawer closed when enter full screen mode */
	@observable fullScreenLeftDrawerClosed = false;

	/** save state of left drawer when entered full screen mode */
	@observable fullScreenLeftDrawerClosedInitially = false;

	/** indicates if in the middle of a mouse click event (mouse is down) */
	@observable isMouseDown = false;

	/** indicates if clicking on image to open lightbox is allowed (mainly to avoid opening lightbox recursively when in Go to Dialog while editing image attributes) */
	@observable restrictLightbox = false;

	/** enter fullscreen mode */
	@action enterFullScreen = (compositeViewName: string): void => {
		this.fullScreen = true;
		this.fullScreenCompositeViewName = compositeViewName;

		// force left drawer in data setup to close when entering full screen mode
		this.fullScreenLeftDrawerClosed = true;
	};

	/** exit fullscreen mode */
	@action exitFullScreen = (): void => {
		this.fullScreen = false;
		this.fullScreenCompositeViewName = "";
		this.fullScreenLeftDrawerClosed = false;
		this.fullScreenLeftDrawerClosedInitially = false;
	};

	/** Add file sizes and abortController to the map */
	@action addToFileUploadMap = (nodeCNL: cnl, attribute: C8AttributeId, fileSize: number, abortController: AbortController): void => {
		const fileInfo:  FileUploadInfo = {
		    abortController,
		    nodeCNL,
		    fileSize,
		    attribute,
		};

		this.fileUploadMap.set(this.getFileUploadMapKey(nodeCNL, attribute), fileInfo);
	};

	/** Deletes an entry from the file upload map */
	@action deleteFromFileUploadMap = (nodeCNL: cnl, attribute: C8AttributeId): void => {
		this.fileUploadMap.delete(this.getFileUploadMapKey(nodeCNL, attribute));
	};

	/** Builds the key for the file upload map based on the cnl and attribute string */
	getFileUploadMapKey = (nodeCNL: cnl, attribute: C8AttributeId): string => {
		return (nodeCNL + "-" + attribute);
	};

	/** Get abortController from the map */
	getFileUploadController = (nodeCNL: cnl, attribute: C8AttributeId): AbortController | undefined => {
		return this.fileUploadMap.get(this.getFileUploadMapKey(nodeCNL, attribute))?.abortController;
	};

	/** Get file size from the map */
	getFileUploadSize = (nodeCNL: cnl, attribute: C8AttributeId): number => {
		const uploadInfo = this.fileUploadMap.get(this.getFileUploadMapKey(nodeCNL, attribute));
		return uploadInfo?.fileSize;
	};

	constructor() {
		makeObservable(this);
	}
}
